import { useCallback, useMemo, useReducer, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { VIEW_ALL_BUILDING, VIEW_ALL_REGION } from '@studio/atomic-ui'
import type { FilterState, SpaceType } from '@studio/atomic-ui'
import { getCapacityValue, validateSpaceType } from './showcase-utils'
import { useBuildings } from '../buildings-provider'
import { ProductShowcaseProps } from '.'

export const defaultFilterState = {
  loading: false,
  disabled: true,
  capacity: { label: '', value: '' },
  duration: { label: '', value: '' },
  location: VIEW_ALL_REGION,
  building: VIEW_ALL_BUILDING,
  date: { from: undefined, to: undefined },
  startTime: { label: '', value: '' },
  instantBook: false,
  isModified: false,
  calendarOpen: false,
}

export const useProductShowcaseStates = (props: ProductShowcaseProps) => {
  const { buildings, getBuildingByKey } = useBuildings()
  const cityName = useCallback(
    (cityKey: string) => buildings.find((b) => b.city.key === cityKey)?.city.name,
    [buildings]
  )
  const getRegionByBuilding = useCallback(
    (key: string) => {
      const city = getBuildingByKey(key)?.city
      return city ? { name: city.name, id: city.key } : null
    },
    [getBuildingByKey]
  )
  const [bookingPanelOptions, setBookingPanelOptions] = useState('')

  // Memoize initial types from props
  const initialTypes = useMemo((): SpaceType[] => {
    if (props.types && props.types.length > 0) {
      return props.types.map((t) => validateSpaceType(t) as SpaceType)
    }
    if (props.type) {
      return [validateSpaceType(props.type) as SpaceType]
    }
    return ['meeting_room']
  }, [props.types, props.type])

  const initialType = useMemo((): SpaceType => {
    if (props.type) {
      return validateSpaceType(props.type) as SpaceType
    }
    return 'meeting_room'
  }, [props.type])

  const [processing, setProcessing] = useState<
    DateRange & {
      types?: SpaceType[] // Track selected types for availability fetching
      type?: SpaceType // Keep for backward compatibility and single-type scenarios
      duration?: string
      startTime?: string
      changedType: boolean
    }
  >({
    from: undefined,
    to: undefined,
    types: initialTypes,
    type: initialType,
    changedType: true,
  })

  const initialRegion = useMemo((): { label: string; value: string; id: string } => {
    const propsRegionName = props.region && !props.building ? cityName(props.region) : undefined
    if (props.region && propsRegionName) {
      return { label: propsRegionName, value: props.region, id: props.region }
    }

    const region = props.building ? getRegionByBuilding(props.building) : null
    if (region) {
      return { label: region.name, value: region.id, id: region.id }
    }

    return VIEW_ALL_REGION
  }, [props.region, props.building, cityName, getRegionByBuilding])

  const initialBuilding = useMemo((): { label: string; value: string; id: string } => {
    const building = props.building ? getBuildingByKey(props.building) : undefined
    const buildingFromProps =
      building && building.city.key === initialRegion.value
        ? { label: building.displayName, value: building.key, id: building.key }
        : null

    if (buildingFromProps) {
      return buildingFromProps
    }

    return {
      label: VIEW_ALL_BUILDING.label,
      value: VIEW_ALL_BUILDING.value,
      id: VIEW_ALL_BUILDING.id || 'all',
    }
  }, [props.building, initialRegion.value, getBuildingByKey])

  // We want to keep a copy for the "Clear filters" button
  const initialRegionFromProps = useMemo(() => initialRegion, [initialRegion])
  const initialBuildingFromProps = useMemo(() => initialBuilding, [initialBuilding])

  const currentRegion = useMemo(() => {
    if (initialRegion.value !== VIEW_ALL_REGION.id) {
      return { id: initialRegion.value, name: cityName(initialRegion.value) }
    }
    return getRegionByBuilding(props.building ?? initialBuilding.value)
  }, [initialRegion.value, props.building, initialBuilding.value, cityName, getRegionByBuilding])

  const keysInCity = (cityKey: string) => buildings.filter((b) => b.city.key === cityKey).map((b) => b.key)

  const buildingList = (state: FilterState): string[] | null => {
    let buildingKeys = null
    // check if page is BDP or PLP
    if (props.isBDP) {
      // BDP
      buildingKeys = [props?.building as string]
    } else if (props.isFlexibleTemplate && props.disableRegionFilter && props.disableBuildingFilter && props.building) {
      buildingKeys = [props?.building as string]
    } else if (props.isFlexibleTemplate && props.disableRegionFilter && props.region) {
      buildingKeys =
        state.building?.id && state.building?.id !== VIEW_ALL_BUILDING.id
          ? [state.building.id]
          : keysInCity(props.region)
    } else {
      // PLP
      if (
        (!state.location?.value && !props.building) ||
        (state.location?.value && !state.building?.id && !props.building) ||
        state.building?.id === VIEW_ALL_BUILDING.id
      ) {
        if (!state.location?.id || state.location?.id === VIEW_ALL_REGION.id) return null
        buildingKeys = keysInCity(state.location.id)
        // if there is a building selected, send that one
      } else if (state.building?.id) {
        buildingKeys = [state.building?.id]
      } else if (props.building) {
        buildingKeys = [props.building]
      }
    }

    return buildingKeys
  }

  // Helper to get types array from state or update, handling backward compatibility
  const getTypesFromState = useCallback((state: FilterState): SpaceType[] => {
    if (state.types && state.types.length > 0) return state.types
    if (state.type) return [state.type]
    return ['meeting_room']
  }, [])

  const [state, handler] = useReducer(
    (currentState: FilterState, update: FilterState) => {
      const currentTypes = getTypesFromState(currentState)

      // Determine updateTypes
      let updateTypes: SpaceType[] = ['meeting_room']
      if (update.types && update.types.length > 0) {
        updateTypes = update.types
      } else if (update.type) {
        updateTypes = [update.type]
      }

      // Check if types changed (for backward compat, also check type)
      const typesChanged =
        (update.types !== undefined &&
          (update.types.length !== currentTypes.length || !update.types.every((t, i) => currentTypes[i] === t))) ||
        (update.type !== undefined && update.type !== currentState.type)

      if (update.building && update.location && typesChanged) {
        setBookingPanelOptions('')
        const newTypes = updateTypes.length > 0 ? updateTypes : currentTypes
        setProcessing({
          from: undefined,
          to: undefined,
          types: newTypes.length > 0 ? newTypes : undefined,
          type: newTypes.length === 1 ? newTypes[0] : undefined, // Backward compat
          changedType: true,
          startTime: '',
          duration: '',
        })
        return {
          ...defaultFilterState,
          types: newTypes.length > 0 ? newTypes : undefined,
          type: newTypes.length === 1 ? newTypes[0] : undefined, // Backward compat
          isModified: true,
        }
      } else {
        const finalTypes = updateTypes.length > 0 ? updateTypes : currentTypes
        setProcessing({
          from: undefined,
          to: undefined,
          types: finalTypes.length > 0 ? finalTypes : undefined,
          type: finalTypes.length === 1 ? finalTypes[0] : undefined, // Backward compat
          changedType: false,
        })
      }
      return {
        ...currentState,
        ...update,
        // Keep the legacy singular type aligned with the canonical types array.
        ...(update.types !== undefined && {
          types: update.types,
          type: update.types.length === 1 ? update.types[0] : undefined,
        }),
        // Backward compat: if type is provided but types isn't, convert type to types
        ...(update.type !== undefined &&
          update.types === undefined && {
            types: [update.type],
            type: update.type,
          }),
        isModified: true,
      }
    },
    (() => {
      // Calculate initial type for backward compat
      let initialTypeForState: SpaceType = 'meeting_room'
      if (props.types && props.types.length === 1) {
        initialTypeForState = validateSpaceType(props.types[0]) as SpaceType
      } else if (props.type) {
        initialTypeForState = validateSpaceType(props.type) as SpaceType
      }

      // Calculate initial capacity
      let initialCapacity = { label: '', value: '' }
      if (props.capacity && props.type) {
        initialCapacity = getCapacityValue(props.type, props.capacity)
      }

      // Calculate initial building
      let buildingLabel = VIEW_ALL_BUILDING.label
      if (props.building) {
        buildingLabel = getBuildingByKey(props.building)?.displayName || buildingLabel
      }

      // Calculate initial location
      let initialLocation = VIEW_ALL_REGION
      if (currentRegion?.name && currentRegion?.id) {
        initialLocation = {
          label: currentRegion.name,
          value: currentRegion.id,
          id: currentRegion.id,
        }
      }

      return {
        ...defaultFilterState,
        disabled: !props.building,
        types: initialTypes,
        type: initialTypeForState,
        capacity: initialCapacity,
        building: {
          label: buildingLabel,
          value: props.building || VIEW_ALL_BUILDING.value,
          id: props.building || VIEW_ALL_BUILDING.id,
        },
        location: initialLocation,
      }
    })()
  )

  // Memoize clearFiltersState to avoid recreating function on every render
  const clearFiltersState = useCallback(() => {
    const currentTypes = getTypesFromState(state)

    // Determine if we should keep initial values (BDP or flexible template with disabled filters)
    const shouldKeepInitialValues =
      props.isBDP || (props.isFlexibleTemplate && props.disableRegionFilter && props.disableBuildingFilter)

    // Use initial values if required, otherwise reset to "View All"
    const resetLocation = shouldKeepInitialValues ? initialRegionFromProps : VIEW_ALL_REGION
    const resetBuilding = shouldKeepInitialValues ? initialBuildingFromProps : VIEW_ALL_BUILDING

    handler({
      loading: false,
      disabled:
        !resetLocation.value ||
        resetLocation?.value === 'all' ||
        !resetBuilding.value ||
        resetBuilding?.value === 'all',
      capacity: {
        label: '',
        value: '',
      },
      duration: {
        label: '',
        value: '',
      },
      date: undefined,
      startTime: {
        label: '',
        value: '',
      },
      location: resetLocation,
      building: resetBuilding,
      instantBook: false,
      types: currentTypes.length > 0 ? currentTypes : undefined, // Keep current types selection
      type: currentTypes.length === 1 ? currentTypes[0] : undefined, // Backward compat
    })
  }, [
    state,
    getTypesFromState,
    props.isBDP,
    props.isFlexibleTemplate,
    props.disableRegionFilter,
    props.disableBuildingFilter,
    initialRegionFromProps,
    initialBuildingFromProps,
  ])

  return {
    state,
    handler,
    buildingListCall: () => buildingList(state),
    processing,
    setProcessing,
    bookingPanelOptions,
    setBookingPanelOptions,
    clearFiltersState,
    initialRegionFromProps,
    initialBuildingFromProps,
  }
}
